import { useState, useEffect, useCallback } from "react";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { fmtDuration, fmtDate, BILLING_COLOR } from "@/lib/supportApi";
import { PencilSimple } from "@phosphor-icons/react";

function Row({ label, children, testid }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-0.5 sm:gap-3 py-1.5">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 sm:w-40 shrink-0">{label}</div>
      <div data-testid={testid} className="text-sm text-zinc-200 min-w-0 break-words">{children ?? <span className="text-zinc-500">—</span>}</div>
    </div>
  );
}

const SOURCE_LABEL = { manual: "Manual entry", timer: "Timer", remote_session: "Remote session" };

export function TimeEntryDetailDialog({ open, onOpenChange, entryId, categories = [], onEdit }) {
  const { currentOrgId } = useAuth();
  const [e, setE] = useState(null);

  const load = useCallback(() => {
    if (!entryId || !currentOrgId) return;
    api.get(`/support/time-entries/${entryId}?org_id=${currentOrgId}`).then((r) => setE(r.data)).catch(() => {});
  }, [entryId, currentOrgId]);

  useEffect(() => {
    if (!open) { setE(null); return; }
    load();
  }, [open, load]);

  const catLabel = (key) => categories.find((c) => c.category_key === key)?.label || key || "—";
  const voided = e && e.status === "voided";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-900 border-white/10 max-w-2xl" data-testid="time-entry-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Time entry details</DialogTitle>
          <DialogDescription className="text-zinc-400 text-sm">
            {e ? <>{e.customer_name || "no customer"} · {fmtDuration(e.actual_seconds)}</> : "Loading…"}
          </DialogDescription>
        </DialogHeader>

        {!e ? (
          <div className="py-8 text-center text-sm text-zinc-500">Loading…</div>
        ) : (
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {voided && (
              <div data-testid="ted-voided" className="mb-3 rounded-lg border border-red-500/30 bg-red-500/5 px-3 py-2 text-sm text-red-300">
                This entry is <strong>voided</strong>.{e.voided_reason ? <> Reason: {e.voided_reason}</> : null}
              </div>
            )}
            <div className="rounded-xl border border-white/10 bg-black/30 px-4 py-2 divide-y divide-white/5">
              <Row label="Customer" testid="ted-customer">{e.customer_name}</Row>
              <Row label="Associated ticket" testid="ted-ticket">{e.ticket ? <>#{e.ticket.ticket_number} {e.ticket.subject}</> : null}</Row>
              <Row label="Technician" testid="ted-technician">{e.technician_name}</Row>
              <Row label="Work description" testid="ted-description">{e.description}</Row>
              <Row label="Category" testid="ted-category">{catLabel(e.category_key)}</Row>
              <Row label="Location" testid="ted-location">{e.location_type}</Row>
              <Row label="Actual working time" testid="ted-actual">{fmtDuration(e.actual_seconds)}</Row>
              <Row label="Billing" testid="ted-billing"><span className={`text-[10px] px-1.5 py-0.5 rounded border ${BILLING_COLOR[e.billing_status]}`}>{e.billing_status}</span></Row>
              <Row label="Notes" testid="ted-notes">{e.notes}</Row>
              <Row label="Source" testid="ted-source">{SOURCE_LABEL[e.source] || e.source}</Row>
              <Row label="Created" testid="ted-created">{fmtDate(e.created_at)}</Row>
              {e.session && (
                <Row label="Remote session" testid="ted-session">
                  {e.session.machine_name} · connection {fmtDuration(e.session.duration_seconds)} · <span className="text-zinc-400">{e.session.review_status}</span>
                </Row>
              )}
            </div>

            <div className="mt-4">
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Correction history</div>
              {e.audit.length === 0 ? (
                <div className="rounded-xl border border-white/10 bg-black/30 px-3 py-3 text-xs text-zinc-500" data-testid="ted-audit-empty">No corrections — original values intact.</div>
              ) : (
                <div className="rounded-xl border border-white/10 bg-black/30 divide-y divide-white/5" data-testid="ted-audit">
                  {e.audit.map((a) => (
                    <div key={a.audit_id} className="px-3 py-2 text-xs">
                      <div className="text-zinc-300"><span className="font-mono text-zinc-500">{a.field}</span>: <span className="text-red-300/80 line-through">{String(a.old_value ?? "—")}</span> → <span className="text-emerald-300/90">{String(a.new_value ?? "—")}</span></div>
                      <div className="text-zinc-500 mt-0.5">{a.actor_name || "—"} · {fmtDate(a.created_at)} · reason: {a.reason || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {e?.can_edit && (
            <Button data-testid="ted-edit-button" variant="outline" onClick={() => onEdit && onEdit()} className="border-white/15 bg-transparent hover:bg-zinc-800">
              <PencilSimple size={15} className="mr-1.5" /> Edit
            </Button>
          )}
          <Button data-testid="ted-close-button" onClick={() => onOpenChange(false)} className="bg-blue-600 hover:bg-blue-500">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
